import React, { useEffect, useState } from 'react';
import './styles/filter-selector.scss';

interface FilterSelectorProps {
    noteTypes: string[];
    selectedTypes: string[];
    onUpdateSelectedTypes: (selected: string[]) => void;
    onClose: () => void;
    onApply: (selectedTypes: string[]) => void;
    title?: string;
}

const FilterSelector: React.FC<FilterSelectorProps> = ({
    noteTypes,
    selectedTypes,
    onUpdateSelectedTypes,
    onClose,
    onApply,
    title = 'Filter note type',
}) => {
    const [localSelectedTypes, setLocalSelectedTypes] = useState<string[]>(selectedTypes.length > 0 ? selectedTypes : noteTypes);
    const [selectAll, setSelectAll] = useState(selectedTypes.length === 0 || selectedTypes.length === noteTypes.length);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        // If no types are selected initially, select all
        if (selectedTypes.length === 0) {
            setLocalSelectedTypes(noteTypes);
            setSelectAll(true);
        } else {
            setLocalSelectedTypes(selectedTypes);
            setSelectAll(selectedTypes.length === noteTypes.length);
        }
    }, [selectedTypes, noteTypes]);

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);

        const newSelected = newSelectAll ? [...noteTypes] : [];
        setLocalSelectedTypes(newSelected);
    };

    const handleTypeSelect = (type: string) => {
        const isSelected = localSelectedTypes.includes(type);
        const newSelected = isSelected
            ? localSelectedTypes.filter(t => t !== type)
            : [...localSelectedTypes, type];

        setLocalSelectedTypes(newSelected);
        setSelectAll(newSelected.length === noteTypes.length);
    };

    const handleApply = () => {
        onUpdateSelectedTypes(localSelectedTypes);
        onApply(localSelectedTypes);
        onClose();
    };

    const handleCancel = () => {
        if (selectedTypes.length === 0) {
            setLocalSelectedTypes(noteTypes);
            setSelectAll(true);
        } else {
            setLocalSelectedTypes(selectedTypes);
            setSelectAll(selectedTypes.length === noteTypes.length);
        }
        onClose();
    };

    const filteredTypes = noteTypes.filter(type =>
        type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="task-fs-filter-overlay">
            <div className="task-fs-container-panel task-fs-open">
                <div className="task-fs-filter-content">
                    <header className="task-fs-header">
                        <div className="task-fs-header__logo-container">
                            <div className="task-fs-header__logo-icon">
                                <div className="task-fs-header__logo-icon-inner cursor-pointer" onClick={handleCancel} />
                            </div>
                            <div className="task-fs-header__logo-text">
                                <h4 className="task-fs-column-header">{title}</h4>
                            </div>
                        </div>
                    </header>

                    <section className="task-fs-toolbar">
                        <div className="task-fs-toolbar__select-all">
                            <div className="task-fs-toolbar__select-all-container">
                                <div className="task-fs-toolbar__select-all-inner">
                                    <div className="task-fs-toolbar__select-all-label">
                                        <div
                                            className={`task-fs-filter-checkbox ${selectAll ? 'task-fs-filter-checkbox--selected' : ''}`}
                                            onClick={handleSelectAll}
                                        >
                                            {selectAll && (
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                                                        stroke="white"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                        <span className="task-fs-filter-text">Select all</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="task-fs-toolbar__search">
                            <div className="task-fs-toolbar__search-container">
                                <div className="task-fs-toolbar__search-inner">
                                <div className="task-fs-toolbar__search-input-container relative">
                                    <div className="task-fs-toolbar__search-input-wrapper relative w-full">
                                        <div className="ico-search" />
                                    <input
                                        type="text"
                                        className="task-fs-toolbar__search-input w-full pr-[24px]" // add padding-right to make room for ✕
                                        placeholder="Search"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />

                                    {searchTerm && (
                                        <button
                                        type="button"
                                        aria-label="Clear search"
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-[6px] top-1/2 -translate-y-1/2 text-[#b8c5cc] hover:text-white text-[14px] leading-none bg-transparent border-none cursor-pointer"
                                        >
                                        ✕
                                        </button>
                                    )}
                                    </div>
                                </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <main className="task-fs-filter-main-content">
                        <ul className="task-fs-filter-type-list">
                            {filteredTypes.map((type) => (
                                <li key={type} className="task-fs-filter-type-item">
                                    <div className="task-fs-filter-type-item__container">
                                        <div className="task-fs-filter-type-item__content">
                                            <div className="task-fs-filter-type-item__checkbox-container">
                                                <div
                                                    className={`task-fs-filter-checkbox ${localSelectedTypes.includes(type) ? 'task-fs-filter-checkbox--selected' : ''}`}
                                                    onClick={() => handleTypeSelect(type)}
                                                >
                                                    {localSelectedTypes.includes(type) && (
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path
                                                                d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                                                                stroke="white"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="task-fs-filter-type-item__name-container">
                                                <span className="task-fs-filter-type-item__name">{type}</span>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </main>

                    <footer className="task-fs-filter-footer">
                        <div className="task-fs-filter-footer__container">
                            <div className="task-fs-filter-footer__selected-count">
                                <span className="task-fs-filter-text">{localSelectedTypes.length} of {noteTypes.length} selected</span>
                            </div>
                            <div className="task-fs-filter-footer__buttons">
                                <button className="task-fs-filter-footer__cancel-button" onClick={handleCancel}>
                                    <span className="task-fs-filter-footer__cancel-button-text">Cancel</span>
                                </button>
                                <button className="task-fs-filter-footer__apply-button" onClick={handleApply}>
                                    <span className="task-fs-filter-footer__apply-button-text">Apply</span>
                                </button>
                            </div>
                        </div>
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default FilterSelector;
